// Pure helpers for the admin Jobs tab. No Firebase imports, so the delete
// plan can be tested without touching a database.

// Records that exist only because of a job. Deleting a job leaves these
// behind unless they are removed with it. League rows and user profiles are
// not in this list: they are shared totals, not job records.
export const LINKED_JOB_COLLECTIONS = Object.freeze([
  'bids',
  'messages',
  'notifications',
  'reviews',
  'wasteTransferNotes',
  'referrals',
]);

// The app only ever writes jobs/{id}/private/details.
export const PRIVATE_JOB_DOC = 'details';

export function jobImagePath(jobId) {
  return `jobs/${jobId}/images`;
}

// Matches how a carrier sees a Yard card: service, then the description.
export function jobDisplayTitle(job) {
  const service = String(job?.serviceType || '').trim();
  const description = String(job?.description || '').trim();
  if (service && description) return `${service} - ${description}`;
  return service || description || 'Untitled job';
}

function coerceDate(value) {
  if (value == null || value === '') return null;
  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  return null;
}

export function createdAtMillis(value) {
  const date = coerceDate(value);
  return date ? date.getTime() : 0;
}

export function formatAdminDate(value) {
  const date = coerceDate(value);
  if (!date) return '—';
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/London',
  });
}

// Preferred collection date is stored as the YYYY-MM-DD the customer picked.
export function jobDateLabel(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return formatAdminDate(value);
}

export function toAdminJobRow(job, user, bidCount) {
  const name = String((user && user.name) || job?.customerName || '').trim();
  const email = String((user && user.email) || job?.customerEmail || job?.email || '').trim();
  const count = bidCount == null || bidCount === ''
    ? (Number(job?.bidCount) || 0)
    : (Number(bidCount) || 0);
  return {
    id: job.id,
    title: jobDisplayTitle(job),
    posterName: name || (job?.customerId ? `Unknown user (${job.customerId})` : '—'),
    posterEmail: email || '—',
    createdLabel: formatAdminDate(job?.createdAt),
    jobDateLabel: jobDateLabel(job?.date),
    status: job?.status || '—',
    bidCount: count,
    paymentStatus: job?.paymentStatus || '',
    createdAtMs: createdAtMillis(job?.createdAt),
  };
}

// Extra warning lines for a job that does not look like an untouched test post.
export function jobDeleteCaution(row) {
  const notes = [];
  const status = row?.status || '';
  if (status && status !== 'Pending' && status !== 'Cancelled' && status !== '—') {
    notes.push(`Status is ${status}`);
  }
  if (row?.paymentStatus === 'paid') notes.push('Payment is marked paid');
  else if (row?.paymentStatus === 'pending') notes.push('A payment is in progress');
  return notes;
}

export function deleteConfirmCopy(count) {
  const n = Number(count) || 0;
  const noun = n === 1 ? 'job' : 'jobs';
  return {
    title: `Delete ${n} ${noun}?`,
    intro: `Only the ${noun} listed below will be deleted. For each one, this also removes its bids, private address details, messages, notifications, reviews, waste transfer notes, referrals, and uploaded photos.`,
    leftover: 'Other jobs are left in place. This cannot be undone.',
    untouched: 'League points and carrier profile totals are not changed.',
    confirm: `Delete ${n} ${noun}`,
    cancel: 'Cancel',
  };
}

// Firestore paths removed for one job. The job document is last so a failed
// batch leaves the job visible and the admin can try again.
export function deletePlan(jobId, docsByCollection = {}) {
  const paths = [];
  LINKED_JOB_COLLECTIONS.forEach((name) => {
    const ids = docsByCollection[name] || [];
    ids.forEach((id) => {
      if (id) paths.push(`${name}/${id}`);
    });
  });
  paths.push(`jobs/${jobId}/private/${PRIVATE_JOB_DOC}`);
  paths.push(`jobs/${jobId}`);
  return paths;
}
